import React, { useState, useEffect, useRef, useContext } from "react";
import { createPortal } from "react-dom";
import API_URL from "../config";
import {
  FaBox,
  FaPlusCircle,
  FaCalendarAlt,
  FaSortNumericUp,
  FaTimes,
  FaSearch,
  FaTrash,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaEye,
  FaEyeSlash,
  FaHistory
} from "react-icons/fa";
import "./Stocks.css";
import { AuthContext } from "./AuthContext";

const HIST_LIMIT = 5;

export default function Stocks({ entrepriseId, siteId, typeUser: propTypeUser }) {
  const { user: ctxUser } = useContext(AuthContext);

  const [produits, setProduits] = useState([]);
  const [mouvements, setMouvements] = useState([]);
  const [totauxApi, setTotauxApi] = useState([]);
  const [aggregated, setAggregated] = useState([]);
  const [produitId, setProduitId] = useState("");
  const [quantite, setQuantite] = useState("");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showHistModal, setShowHistModal] = useState(false);
  const [histProduit, setHistProduit] = useState(null);
  const [histRows, setHistRows] = useState([]);
  const [showAllHist, setShowAllHist] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDate, setDeleteDate] = useState("");
  const [customDialog, setCustomDialog] = useState(null); 
  const firstFieldRef = useRef(null);

  const userType = propTypeUser || ctxUser?.type_user || null;
  const isGestionnaire = userType === "gestionnaire";
  const canAdd = isGestionnaire;
  const canDelete = isGestionnaire;

  const effectiveSiteId = isGestionnaire ? ctxUser?.siteId : siteId;

  const showCustomDialog = (type, message, action = null, payload = null) => {
      setCustomDialog({ type, message, action, payload });
  };
  const closeCustomDialog = () => {
      setCustomDialog(null);
  };
  const handleDialogAction = () => {
      if (customDialog?.action) {
          customDialog.action(customDialog.payload);
      }
      closeCustomDialog();
  };

  useEffect(() => {
    if (!entrepriseId || !effectiveSiteId) return;
    fetch(`${API_URL}/api/produits?entrepriseId=${entrepriseId}&siteId=${effectiveSiteId}`)
      .then((res) => res.json())
      .then((data) => setProduits(Array.isArray(data) ? data : []))
      .catch(() => setProduits([]));
  }, [entrepriseId, effectiveSiteId]);

  const fetchAllStocks = async () => {
    if (!entrepriseId || !effectiveSiteId) return;
    try {
      const [resMouv, resTot] = await Promise.all([
        fetch(`${API_URL}/api/stocks/${entrepriseId}/${effectiveSiteId}`),
        fetch(`${API_URL}/api/stocks/totaux/${entrepriseId}/${effectiveSiteId}`),
      ]);

      const mouv = await resMouv.json();
      const tot = await resTot.json();

      setMouvements(Array.isArray(mouv) ? mouv : []);
      setTotauxApi(Array.isArray(tot) ? tot : []);

      const map = new Map();

      tot.forEach((t) => {
        const pid = String(t.produit_id);
        map.set(pid, {
          produit_id: pid,
          produitNom: t.produitNom ?? t.nom,
          produitDenomination: t.produitDenomination ?? "",
          totalAdded: 0,
          totalRemaining: Number(t.stock_total) || 0,
          lastAddDate: null,
          history: [],
        });
      });

      mouv.forEach((r) => {
        const pid = String(r.produit_id);
        if (!map.has(pid)) {
          map.set(pid, {
            produit_id: pid,
            produitNom: r.produitNom ?? r.nom,
            produitDenomination: r.produitDenomination ?? r.denomination ?? "",
            totalAdded: 0,
            totalRemaining: Number(r.stock_restant) || 0,
            lastAddDate: null,
            history: [],
          });
        }

        const entry = map.get(pid);
        entry.totalAdded += Number(r.quantite) || 0;
        entry.history.push(r);
        
        const dateMaj = r.date_maj ? new Date(r.date_maj) : null;
        if (dateMaj && (!entry.lastAddDate || dateMaj > entry.lastAddDate)) {
          entry.lastAddDate = dateMaj;
        }
        
        if (!entry.produitDenomination && (r.produitDenomination || r.denomination)) {
            entry.produitDenomination = r.produitDenomination || r.denomination;
        }
      });

      setAggregated(
        Array.from(map.values()).sort((a, b) =>
          a.produitNom.localeCompare(b.produitNom)
        )
      );
    } catch (e) {
      console.error(e);
      setAggregated([]);
    }
  };

  useEffect(() => {
    fetchAllStocks();
  }, [entrepriseId, effectiveSiteId]);

  useEffect(() => {
    if (showForm) {
      setTimeout(() => {
        try {
          if (firstFieldRef.current) firstFieldRef.current.focus();
        } catch {}
      }, 50);
    }
  }, [showForm]);

  const openForm = () => {
    if (!canAdd) {
      return showCustomDialog('error', "Vous n'avez pas la permission d'ajouter des stocks.");
    }
    setDate(new Date().toISOString().slice(0, 10));
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setProduitId("");
    setQuantite("");
    setDate("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!produitId || quantite === "" || !date) {
      return showCustomDialog('error', "Veuillez remplir tous les champs requis.");
    }

    try {
      setLoading(true);
      const payload = { produitId, quantite, date, entrepriseId, siteId: effectiveSiteId };
      const resp = await fetch(`${API_URL}/api/stocks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();

      if (!resp.ok) {
        showCustomDialog('error', data?.error || "Erreur lors de l'ajout.");
        return;
      }

      showCustomDialog('success', data.message || "Stock ajouté avec succès !");
      closeForm();
      fetchAllStocks();
    } catch (err) {
      showCustomDialog('error', "Erreur de connexion.");
    } finally {
      setLoading(false);
    }
  };

  const openHistorique = (prod) => {
    const rows = mouvements
      .filter((r) => String(r.produit_id) === String(prod.produit_id))
      .sort((a, b) => new Date(b.date_maj) - new Date(a.date_maj));
    
    setHistRows(rows);
    setHistProduit(prod);
    setShowAllHist(false);
    setShowHistModal(true);
  };

  const closeHistorique = () => {
    setShowHistModal(false);
    setHistProduit(null);
    setHistRows([]);
    setShowAllHist(false);
  };
  
  const toggleShowAllHist = () => setShowAllHist((prev) => !prev);
  
  const displayedHistRows = showAllHist ? histRows : histRows.slice(0, HIST_LIMIT);
  const shouldShowToggleButton = histRows.length > HIST_LIMIT;

  const askDelete = (id) => {
    if (!canDelete) return showCustomDialog('error', "Permission refusée.");
    setDeleteId(id);
    setDeleteDate(new Date().toISOString().slice(0, 10));
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteDate) return showCustomDialog('error', "Choisissez une date.");
    setShowDeleteModal(false);
    showCustomDialog('confirm', `Confirmer la suppression ?`, executeDelete, deleteId);
  };
  
  const executeDelete = async (idToDelete) => {
    try {
      setLoading(true);
      const resp = await fetch(
        `${API_URL}/api/stocks/${idToDelete}?type_user=${userType}&user_id=${ctxUser?.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ date_suppression: deleteDate }),
        }
      );
      if (!resp.ok) throw new Error();
      await fetchAllStocks();
      setHistRows((prev) => prev.filter((r) => String(r.id) !== String(idToDelete))); 
      showCustomDialog('success', "Supprimé avec succès.");
    } catch (e) {
      showCustomDialog('error', "Erreur lors de la suppression.");
    } finally {
      setLoading(false);
    }
  };

  const filteredAggregated = aggregated.filter((a) =>
    a.produitNom.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const CustomDialog = () => {
    if (!customDialog) return null;
    const { type, message } = customDialog;
    let icon, title, btnClass = "btn-primary";

    if (type === 'success') {
        icon = <FaCheckCircle className="status-icon success" />;
        title = "Succès";
    } else if (type === 'error') {
        icon = <FaTimes className="status-icon error" />;
        title = "Erreur";
    } else if (type === 'confirm') {
        icon = <FaExclamationTriangle className="status-icon warning" />;
        title = "Confirmation";
    }

    return (
        <div className="modal-overlay" onClick={closeCustomDialog}>
            <div className="modal-content small dialog-box" onClick={(e) => e.stopPropagation()}>
                <div className="dialog-header">{icon}<h3>{title}</h3></div>
                <p className="dialog-msg">{message}</p>
                <div className="modal-actions centered">
                    {type === 'confirm' && <button className="btn-cancel" onClick={closeCustomDialog}>Annuler</button>}
                    <button className={btnClass} onClick={handleDialogAction}>
                        {type === 'confirm' ? "Confirmer" : "OK"}
                    </button>
                </div>
            </div>
        </div>
    );
  };

  return (
    <div className="stocks-page">
      <header className="stocks-header">
        <div className="title-section">
          <div className="icon-badge"><FaBox /></div>
          <h1>Gestion des Stocks</h1>
        </div>
        
        <div className="stocks-toolbar">
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {canAdd && (
            <button className="btn-add-stock" onClick={openForm} disabled={loading}>
              <FaPlusCircle /> <span>Ajouter du stock</span>
            </button>
          )}
        </div>
      </header>

      {!canAdd && userType === "entreprise" && (
          <div className="permission-banner">
              <FaInfoCircle /> Seuls les gestionnaires peuvent modifier les stocks.
          </div>
      )}

      <div className="table-card">
        <div className="table-responsive">
          <table className="stocks-table">
            <thead>
              <tr>
                <th>Produit</th>
                <th>Stock ajouté</th>
                <th>Stock restant</th>
                <th className="date-cell-header">Dernière date</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredAggregated.length === 0 ? (
                <tr><td colSpan={5} className="empty-cell">Aucun produit trouvé</td></tr>
              ) : (
                filteredAggregated.map((p) => (
                  <tr key={p.produit_id}>
                    <td>
                      <div className="prod-info">
                        <span className="prod-name">{p.produitNom}</span>
                        {p.produitDenomination && <span className="prod-denom">{p.produitDenomination}</span>}
                      </div>
                    </td>
                    <td><span className="badge badge-blue">{p.totalAdded}</span></td>
                    <td><span className={`badge ${p.totalRemaining > 5 ? 'badge-green' : 'badge-red'}`}>{p.totalRemaining}</span></td>
                    <td className="date-cell">{p.lastAddDate ? p.lastAddDate.toLocaleDateString() : "—"}</td>
                    <td className="text-center">
                      <button className="btn-info-circle pulse-animation" onClick={() => openHistorique(p)} title="Voir l'historique">
                        <FaInfoCircle />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && createPortal(
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content side-panel" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeForm}><FaTimes /></button>
            <h3 className="modal-header-title"><FaPlusCircle /> Nouveau Mouvement</h3>
            <form onSubmit={handleSubmit} className="stocks-form">
              <div className="form-group">
                <label>Produit cible</label>
                <div className="input-field">
                  <FaBox className="field-icon" />
                  <select ref={firstFieldRef} value={produitId} onChange={(e) => setProduitId(e.target.value)}>
                    <option value="">Sélectionnez un produit</option>
                    {produits.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Quantité à ajouter</label>
                <div className="input-field">
                  <FaSortNumericUp className="field-icon" />
                  <input type="number" min="0" value={quantite} onChange={(e) => setQuantite(e.target.value)} placeholder="0" />
                </div>
              </div>
              <div className="form-group">
                <label>Date de réception</label>
                <div className="input-field">
                  <FaCalendarAlt className="field-icon" />
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-submit">
                {loading ? <FaSpinner className="spin-icon" /> : <FaCheckCircle />} 
                {loading ? "Traitement..." : "Enregistrer le stock"}
              </button>
            </form>
          </div>
        </div>, document.body
      )}

      {showHistModal && createPortal(
        <div className="modal-overlay" onClick={closeHistorique}>
          <div className="modal-content wide scrollable-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeHistorique}><FaTimes /></button>
            <div className="hist-header">
                <FaHistory className="hist-icon" />
                <h3>Historique : {histProduit?.produitNom}</h3>
            </div>
            
            <div className="hist-body">
              <div className="table-responsive">
                <table className="stocks-table mini">
                  <thead>
                    <tr>
                      <th>Qté</th>
                      <th>Stock Final</th>
                      <th>Date & Heure</th>
                      {canDelete && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedHistRows.map((h) => (
                      <tr key={h.id}>
                        <td><strong>+{h.quantite}</strong></td>
                        <td>{h.stock_restant}</td>
                        <td className="hist-date-cell">{h.date_maj ? new Date(h.date_maj).toLocaleString() : "—"}</td>
                        {canDelete && (
                          <td>
                            <button className="btn-row-delete" onClick={() => askDelete(h.id)}><FaTrash /></button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {shouldShowToggleButton && (
                <div className="hist-footer">
                  <button className="btn-text" onClick={toggleShowAllHist}>
                    {showAllHist ? <FaEyeSlash /> : <FaEye />} {showAllHist ? "Voir moins" : `Tout voir (${histRows.length})`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>, document.body
      )}

      {showDeleteModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-header-title">Confirmer suppression</h3>
            <p className="sub-text">Veuillez valider la date de retrait :</p>
            <input type="date" value={deleteDate} onChange={(e) => setDeleteDate(e.target.value)} className="modern-date-input" />
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Annuler</button>
              <button className="btn-delete" onClick={confirmDelete}>Supprimer</button>
            </div>
          </div>
        </div>, document.body
      )}
      {createPortal(<CustomDialog />, document.body)}
    </div>
  );
}